/* npm modules outside of vrbo */
import React, {Fragment} from 'react';
import PropTypes from 'prop-types';

/* Atoms (baseline) */
import {
    Avatar,
    MAIL__24,
    MORE__24,
    SVGIcon,
    TRASHCAN__24,
} from '../../../components/atoms/baseline/baseline';

/* Molecules (baseline) */
import {
    edap,
    Experiment,
    LoadingOverlay,
    Tooltip,
} from '../../../components/molecules/baseline/baseline';

/* Utils */
import translate from '../../../utils/translations';

/* Constants */
import {TRAVELER_ACTIVITY, INVITE_GUEST_RESEND} from './constants';

/* Styles */
import './Guest.less';

export const Guest = ({
    acceptedInvite,
    email,
    firstName,
    fullName,
    isLoading,
    isPrimary,
    lastName,
    publicImageUrl,
    removeGuest,
    resendEmail,
    translations,
}) => {
    const PRIMARY_GUEST_LABEL = translate(
        translations,
        'trav-dash/trav-home',
        'messages',
        'th.inbox.conversation.add.guests.primary'
    );
    const PAYMENT_PENDING_LABEL = translate(
        translations,
        'trav-dash/trav-home',
        'messages',
        'th.inbox.conversation.quoteSummary.payment.status.PENDING'
    );
    const PRIMARY_BOOKER_LABEL = translate(
        translations,
        'trav-dash/trav-home',
        'messages',
        'widget.trip.gog.copy.update.postInvite.primary.booker.label'
    );
    const RESEND_EMAIL_LABEL = translate(
        translations,
        'trav-dash/trav-home',
        'messages',
        'th.button.resend.email'
    );
    const REMOVE_GUEST_LABEL = translate(
        translations,
        'trav-dash/trav-home',
        'messages',
        'th.button.remove'
    );

    const ListItem = ({children, ...rest}) => (
        <li className="li--flex-icon Guest__options-link" {...rest}>
            {children}
        </li>
    );
    const ListIcon = ({children}) => (
        <span className="li__icon">{children}</span>
    );
    const ListContent = ({children}) => (
        <span className="li__content">{children}</span>
    );

    const getSecondaryInfo = () => {
        if (isPrimary) {
            return PRIMARY_GUEST_LABEL;
        }

        if (!acceptedInvite) {
            return PAYMENT_PENDING_LABEL;
        }

        return email;
    };

    const getSecondaryInfoCopyChanged = () => {
        if (isPrimary) {
            return PRIMARY_BOOKER_LABEL;
        }

        if (!acceptedInvite) {
            return PAYMENT_PENDING_LABEL;
        }

        return email;
    };

    const getPrimaryInfo = () => {
        if (firstName?.length > 1) {
            return fullName;
        }

        return email;
    };

    const resendEmailHandler = () => {
        edap().push({
            name: 'generic.event',
            data: {
                eventcategory: TRAVELER_ACTIVITY,
                eventaction: INVITE_GUEST_RESEND,
            },
        });

        resendEmail(
            [
                {
                    email,
                },
            ],
            true
        );
    };

    const renderTooltipContent = () => {
        if (isLoading) {
            return <LoadingOverlay />;
        }

        return (
            <ul className="list-unstyled list--vertical-grid text-brand Guest__options-list">
                {!acceptedInvite && (
                    <ListItem onClick={resendEmailHandler}>
                        <ListIcon>
                            <SVGIcon markup={MAIL__24} />
                        </ListIcon>
                        <ListContent>{RESEND_EMAIL_LABEL}</ListContent>
                    </ListItem>
                )}
                <ListItem onClick={() => removeGuest({email})}>
                    <ListIcon>
                        <SVGIcon markup={TRASHCAN__24} />
                    </ListIcon>
                    <ListContent>{REMOVE_GUEST_LABEL}</ListContent>
                </ListItem>
            </ul>
        );
    };

    return (
        <div className="Guest position-relative">
            <div className="Guest__info-container">
                {
                    <Avatar
                        dataObject={{
                            firstName,
                            lastName,
                            publicImageUrl,
                        }}
                        hasTooltip={false}
                        size={'32'}
                        usePlaceholder
                    />
                }
                <div className="Guest__info">
                    <strong className="Guest__info-text-primary">
                        {getPrimaryInfo()}
                    </strong>
                    <p className="Guest__info-text-secondary">
                        <Experiment
                            name={'vrbo_stx_gog_widget_copy_update'}
                            deferLogging
                        >
                            {({On, Off}) => {
                                return (
                                    <Fragment>
                                        <On>
                                            <small>
                                                {getSecondaryInfoCopyChanged()}
                                            </small>
                                        </On>
                                        <Off>
                                            <small>{getSecondaryInfo()}</small>
                                        </Off>
                                    </Fragment>
                                );
                            }}
                        </Experiment>
                    </p>
                </div>
            </div>
            <div>
                {!isPrimary && (
                    <Tooltip
                        centered
                        content={renderTooltipContent()}
                        interactiveContent
                        placement={'left'}
                        tooltipType={'popover'}
                        trigger={'hover focus'}
                    >
                        <SVGIcon
                            className={'Guest__options'}
                            inlineFlex
                            markup={MORE__24}
                            useFill
                        />
                    </Tooltip>
                )}
            </div>
        </div>
    );
};

Guest.propTypes = {
    acceptedInvite: PropTypes.bool,
    email: PropTypes.string,
    firstName: PropTypes.string,
    fullName: PropTypes.string,
    isLoading: PropTypes.bool,
    isPrimary: PropTypes.bool,
    lastName: PropTypes.string,
    publicImageUrl: PropTypes.func,
    removeGuest: PropTypes.func,
    resendEmail: PropTypes.func,
    translations: PropTypes.shape({}),
};
